// api/providers/seriesFavorites.ts
import { playListPassword, playListUsername, playListUrl } from "../../../state";
import { convertItemsToTilesSeries } from "../formatters/ItemFormatter";
import { fetchData } from "../workers/fetchWorkerManager";
import { dnsResolver } from "./dnsResolver";

const favoritesCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000;

// ✅ ADDED: Helper to get current playlist identifier
const getCurrentPlaylistId = () => {
  return `${playListUrl()}_${playListUsername()}`;
};

// ✅ MODIFIED: Filter by current playlist
const getFavoriteSeries = () => {
  try {
    const data = localStorage.getItem("series_favorites");
    if (!data) return [];

    const allFavorites = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();

    // Filter only favorites from current playlist
    const playlistFavorites = allFavorites.filter(item => item.playlistId === currentPlaylist);

    return playlistFavorites.sort((a, b) => b.addedTimestamp - a.addedTimestamp);
  } catch (error) {
    console.error("Error reading series favorites:", error);
    return [];
  }
};

const buildApiUrl = async (baseUrl, username, password, action, additionalParams = "") => {
  let apiUrl = `${baseUrl}/player_api.php?username=${username}&password=${password}&action=${action}`;
  if (additionalParams) {
    apiUrl += `&${additionalParams}`;
  }

  let finalApiUrl = apiUrl;
  try {
    const dnsResult = await dnsResolver.transformUrl(apiUrl);
    if (typeof dnsResult === "object" && dnsResult.url) {
      finalApiUrl = dnsResult.url;
    } else if (typeof dnsResult === "string" && dnsResult !== apiUrl) {
      finalApiUrl = dnsResult;
    }
  } catch (dnsError) {
    console.warn("DNS transformation failed:", dnsError);
  }
  return finalApiUrl;
};

export default function (filter) {
  return async pageIndex => {
    const username = playListUsername();
    const password = playListPassword();
    const baseUrl = playListUrl();

    if (!username || !password || !baseUrl) {
      console.error("Missing required parameters for Series Favorites");
      return [];
    }

    const cacheKey = `${baseUrl}_${username}_series_favorites`;

    const cached = favoritesCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log("Using cached series favorites data");
      return cached.data;
    }

    console.log("Loading Series Favorites...");

    try {
      const favoritesList = getFavoriteSeries();

      if (favoritesList.length === 0) {
        console.log("No favorite series - returning empty array");
        return [];
      }

      console.log(`Found ${favoritesList.length} favorite series`);

      const seriesIds = new Set();
      const categoryIds = new Set();

      favoritesList.forEach(item => {
        if (item.series_id) seriesIds.add(item.series_id);
        if (item.category_id) categoryIds.add(item.category_id);
      });

      const allSeries = [];

      for (const categoryId of categoryIds) {
        try {
          const categoryUrl = await buildApiUrl(
            baseUrl,
            username,
            password,
            "get_series",
            `category_id=${categoryId}`,
          );
          const series = await fetchData(categoryUrl);
          if (Array.isArray(series)) {
            allSeries.push(...series);
          }
        } catch (error) {
          console.warn(`Failed to fetch series category ${categoryId}:`, error);
        }
      }

      const matchedSeries = [];

      for (const favItem of favoritesList) {
        try {
          const seriesData = allSeries.find(s => String(s.series_id) === String(favItem.series_id));

          if (seriesData) {
            matchedSeries.push({
              ...seriesData,
              favorite_timestamp: favItem.addedTimestamp,
            });
          } else {
            matchedSeries.push({
              series_id: favItem.series_id,
              name: favItem.name || `Series ${favItem.series_id}`,
              cover: favItem.poster || "",
              category_id: favItem.category_id || "unknown",
              rating_5based: favItem.rating || 0,
              favorite_timestamp: favItem.addedTimestamp,
            });
          }
        } catch (err) {
          console.warn("Error processing favorite series:", err);
        }
      }

      const limitedSeries = matchedSeries.slice(0, 50);
      const processedTiles = convertItemsToTilesSeries(limitedSeries);

      favoritesCache.set(cacheKey, {
        data: processedTiles,
        timestamp: Date.now(),
      });

      console.log(`Series favorites processed: ${processedTiles.length} tiles`);
      return processedTiles;
    } catch (error) {
      console.error("Error getting Series Favorites:", error);
      return [];
    }
  };
}

// ✅ MODIFIED: Add playlistId when saving
export const addSeriesToFavorites = seriesData => {
  try {
    const data = localStorage.getItem("series_favorites");
    let favoritesList = data ? JSON.parse(data) : [];

    const currentPlaylist = getCurrentPlaylistId();

    const existingIndex = favoritesList.findIndex(
      item => String(item.series_id) === String(seriesData.series_id) && item.playlistId === currentPlaylist,
    );

    if (existingIndex !== -1) {
      console.log("Series already in favorites");
      return false;
    }

    const entry = {
      series_id: seriesData.series_id,
      name: seriesData.name || "",
      poster: seriesData.poster || "",
      category_id: seriesData.category_id || "",
      rating: seriesData.rating || 0,
      addedTimestamp: Date.now(),
      playlistId: currentPlaylist, // ✅ ADDED THIS LINE
    };

    favoritesList.push(entry);

    if (favoritesList.length > 100) {
      favoritesList = favoritesList.slice(-100);
    }

    localStorage.setItem("series_favorites", JSON.stringify(favoritesList));
    favoritesCache.clear();

    console.log("Added series to favorites:", seriesData.name);
    return true;
  } catch (error) {
    console.error("Error adding series to favorites:", error);
    return false;
  }
};

// ✅ MODIFIED: Remove only from current playlist
export const removeSeriesFromFavorites = seriesId => {
  try {
    const data = localStorage.getItem("series_favorites");
    if (!data) return false;

    let favoritesList = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();
    const initialLength = favoritesList.length;

    // Remove only from current playlist
    favoritesList = favoritesList.filter(
      item => !(String(item.series_id) === String(seriesId) && item.playlistId === currentPlaylist),
    );

    if (favoritesList.length === initialLength) {
      console.log("Series not found in favorites");
      return false;
    }

    localStorage.setItem("series_favorites", JSON.stringify(favoritesList));
    favoritesCache.clear();

    console.log("Removed series from favorites");
    return true;
  } catch (error) {
    console.error("Error removing series from favorites:", error);
    return false;
  }
};

// ✅ MODIFIED: Check only in current playlist
export const isSerieFavorite = seriesId => {
  try {
    const data = localStorage.getItem("series_favorites");
    if (!data) return false;

    const favoritesList = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();

    return favoritesList.some(
      item => String(item.series_id) === String(seriesId) && item.playlistId === currentPlaylist,
    );
  } catch (error) {
    console.error("Error checking if series is favorite:", error);
    return false;
  }
};

export const clearSeriesFavorites = () => {
  localStorage.removeItem("series_favorites");
  favoritesCache.clear();
  console.log("Cleared all series favorites");
};

// ✅ MODIFIED: Check only current playlist
export const hasAnyFavoriteSeries = () => {
  try {
    const data = localStorage.getItem("series_favorites");
    if (!data) return false;

    const list = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();

    return list.some(item => item.playlistId === currentPlaylist);
  } catch {
    return false;
  }
};
